import { useState, useEffect } from 'react';
import { apiRequest } from '../apiClient';

// Types for the real analytics API responses
export interface PlatformAnalytics {
  total_organizations: number;
  total_users: number;
  total_credentials: number;
  total_templates: number;
  organizations: Array<{
    org_id: string;
    org_name: string;
    user_count: number;
    credential_count: number;
    template_count: number;
  }>;
}

export interface OrganizationAnalytics {
  organization_id: string;
  organization_name: string;
  total_users: number;
  total_credentials: number;
  total_templates: number;
}

export interface AnalyticsResponse {
  view_type: 'platform' | 'organization';
  data: PlatformAnalytics | OrganizationAnalytics;
}

export interface UseAnalyticsReturn {
  platformData: PlatformAnalytics | null;
  organizationData: OrganizationAnalytics | null;
  isLoading: boolean;
  error: string | null;
  refreshData: () => Promise<void>;
  getOrganizationAnalytics: (orgId: string) => Promise<void>;
}

export function useAnalytics(): UseAnalyticsReturn {
  const [platformData, setPlatformData] = useState<PlatformAnalytics | null>(null);
  const [organizationData, setOrganizationData] = useState<OrganizationAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPlatformAnalytics = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response: AnalyticsResponse = await apiRequest('/api/admin/analytics/dashboard?view_type=platform');
      if (response.view_type === 'platform') {
        setPlatformData(response.data as PlatformAnalytics);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch platform analytics');
      console.error('Error fetching platform analytics:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOrganizationAnalytics = async (orgId: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const response: AnalyticsResponse = await apiRequest(`/api/admin/analytics/dashboard?view_type=organization&org_id=${orgId}`);
      if (response.view_type === 'organization') {
        setOrganizationData(response.data as OrganizationAnalytics);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch organization analytics');
      console.error('Error fetching organization analytics:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshData = async () => {
    await fetchPlatformAnalytics();
  };

  const getOrganizationAnalytics = async (orgId: string) => {
    await fetchOrganizationAnalytics(orgId);
  };

  // Fetch platform analytics on mount
  useEffect(() => {
    fetchPlatformAnalytics();
  }, []);

  return {
    platformData,
    organizationData,
    isLoading,
    error,
    refreshData,
    getOrganizationAnalytics,
  };
} 